import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom'
import { DarkModeProvider } from './context/DarkModeContext'
import { motion } from 'framer-motion'
import './styles/darkMode.css'

import Navbar from './components/Navbar/Navbar'
import Hero from './components/Hero/Hero'
import Integration from './components/Integration/Integration'
import WhyGenix from './components/WhyGenix/WhyGenix'
import Features from './components/Features/Features'
import PlatformSection from './components/PlatformSection/PlatformSection'
import Testimonials from './components/Testimonials/Testimonials'
import Updates from './components/Updates/Updates'
import Pricing from './components/Pricing/Pricing'
import GenixPendant from './components/GenixPendant/GenixPendant'
import FAQ from './components/FAQ/FAQ'
import Consultation from './components/Consultation/Consultation'
import Footer from './components/Footer/Footer'
import SchedulingShowcase from './components/SchedulingShowcase/SchedulingShowcase'
import StatisticsGrid from './components/StatisticsGrid/StatisticsGrid'
import SignUp from './components/SignUp/SignUp'
import Login from './components/Login/Login'
import './App.css'
import { ProductHero } from './components/Hero/Hero'
import ProductStats from './components/ProductStats/ProductStats'
import ProductHowItWorks from './components/ProductHowItWorks/ProductHowItWorks'
import ProductFeature from './components/ProductFeature/ProductFeature'
import ProductOnboardingFlow from './components/ProductOnboardingFlow/ProductOnboardingFlow'
import ProductShiftTracking from './components/ProductShiftTracking/ProductShiftTracking'
import ProductComplianceMonitoring from './components/ProductComplianceMonitoring/ProductComplianceMonitoring'
import ProductGDPR from './components/ProductHowItWorks/ProductGDPR/ProductGDPR'
import ProductPayrollComplexity from './components/ProductPayrollComplexity/ProductPayrollComplexity'
import ProductImageCarousel from './components/ProductImageCarousel/ProductImageCarousel'
import ProductWhyGenix from './components/ProductWhyGenix/ProductWhyGenix'
import ContactUs from './components/ContactUs/ContactUs'
import Location from './components/Location/Location'
import DeviceOverview from './components/DeviceOverview/DeviceOverview'
import DeviceFeatures from './components/DeviceFeatures/DeviceFeatures'
import KeyUseCases from './components/KeyUseCases/KeyUseCases'
import FutureEnhancements from './components/FutureEnhancements/FutureEnhancements'
import TechnologyStack from './components/TechnologyStack/TechnologyStack'
import BookDemoHero from './components/BookDemoHero/BookDemoHero'
import BookDemoTeamSection from './components/BookDemoTeamSection/BookDemoTeamSection'
import WhyGenixRota from './components/WhyGenix/WhyGenixRota'
import KeyUseCasesRota from './components/KeyUseCases/KeyUseCasesRota'
import HowItWorksRota from './components/ProductHowItWorks/HowItWorksRota'
import RoataPricingCTA from './components/Pricing/RoataPricingCTA'
import PricingRoute from './components/PricingRoute'
import ResourcesRoute from './components/ResourcesRoute'
import TryGenixPendant from './components/GenixPendant/TryGenixPendant'

function App() {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isPendantFixed, setIsPendantFixed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const pendantSection = document.querySelector('.pendant-section');
      const windowHeight = window.innerHeight;
      const isWideScreen = window.innerWidth > 1200;
      
      if (pendantSection) {
        const pendantRect = pendantSection.getBoundingClientRect();
        // Check if pendant section is in view
        if (pendantRect.top <= 0) {
          setIsPendantFixed(true);
        } else {
          setIsPendantFixed(false);
        }
        
        // Only update scroll position for wide screens
        if (isWideScreen) {
          // Update general scroll position
          const scrollPos = window.scrollY / windowHeight;
          setScrollPosition(Math.min(Math.max(scrollPos, 0), 1));
        } else {
          // For smaller screens, reset scroll position
          setScrollPosition(0);
        }
      }
    };
    
    // Initial check for screen size
    const checkScreenSize = () => {
      const isWideScreen = window.innerWidth > 1200;
      if (!isWideScreen) {
        // Reset scroll position for smaller screens
        setScrollPosition(0);
      }
    };
    
    // Check screen size on mount and resize
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    window.addEventListener('scroll', handleScroll);
    
    // Loader logic
    const timer = setTimeout(() => setLoading(false), 1200); // 1.2s fake load
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkScreenSize);
      clearTimeout(timer);
    };
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        minWidth: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        zIndex: 9999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}>
        <img src="/Genixloader.gif" alt="Loading..." style={{ width: 200, height: 200 }} />
      </div>
    );
  }

  return (
    <DarkModeProvider>
      <Router>
        <div className="app">
          <Navbar />
          <main className="main-app-content">
            <Routes>
              <Route path="/" element={
                <>
                  <div className="home-layout">
                    <div className="hero-fixed-background">
                      <Hero />
                    </div>
                    <div className="scrolling-content">
                      <Integration />
                      <div className="content-wrapper visible">
                        <WhyGenix />
                        <SchedulingShowcase />
                        <div className="pendant-section" style={{display:'flex',justifyContent:'center'}}>
                          <GenixPendant isFixed={isPendantFixed} />
                        </div>
                        <div className="feat-new">
                        <Features />
                        </div>
                        
                        <PlatformSection />
                        <Testimonials />
                        <div className="static-grid" style={{display:'flex',justifyContent:'center'}}>
                        <StatisticsGrid />
                        </div>
                        
                        <Updates />
                        <Pricing />
                        
                        <FAQ />
                        <Consultation />
                        <Footer />
                      </div>
                    </div>
                  </div>
                </>
              } />
              <Route path="/products/genix-hr" element={<>
                <div className="page-layout">
                  <div className="hero-fixed-background">
                    <ProductHero
                      title={<>Smarter <span style={{ color: "#00AEEF" }}>HR</span> for<br />Smarter Healthcare</>}
                      description="" // No specific description provided for Roata, leaving empty for now or could infer later
                      imageSrc="/images/HeroProductImg.png"
                      imageAlt="HR Dashboard"
                    />
                  </div>
                  <div className="scrolling-content">
                    <ProductStats />
                    <ProductHowItWorks />
                    <ProductFeature
                      image="/images/HeroProductImg.png"
                      title="Automated Onboarding"
                      description="Set up new staff in minutes with pre-built role templates, automated document collection, and digital signatures — all streamlined for hospitals and clinics."
                    />
                    <ProductOnboardingFlow />
                    <ProductShiftTracking />
                    <ProductComplianceMonitoring />
                    <ProductGDPR />
                    <ProductPayrollComplexity />
                    <ProductImageCarousel />
                    <Testimonials />
                    <ProductWhyGenix />
                    <Consultation />
                    <Footer />
                  </div>
                </div>
              </>} />
              <Route path="/products/genix-roata" element={<>
                <div className="page-layout">
                  <div className="hero-fixed-background">
                    <section className="genix-hero changed-genix">
                      <div className="genix-container">
                        <div className="genix-hero-content" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '40px', flexWrap: 'wrap'}}>
                          <div className="genix-hero-text genix-roata-hero-text animate-slide-left" style={{flex: 1, minWidth: 320}}>
                            <h1 style={{fontSize: '3rem', fontWeight: 700, marginBottom: '1.5rem'}}>Smarter Scheduling<br/>with Genix Rota</h1>
                            <p style={{fontSize: '1.2rem', color: '#334155', marginBottom: '2.5rem', maxWidth: 500}}>
                              Turn written notes or spreadsheets into smart, AI-generated schedules in seconds — no manual work needed. Genix Rota simplifies complex planning with intelligent automation, saving time and reducing errors.
                            </p>
                            <div className="roata-hero-buttons" style={{display: 'flex', gap: '20px'}}>
                              <a
                                href="#keyusecases-roata-container"
                                className="genix-btn genix-btn-outline"
                                style={{minWidth: 150}}
                                onClick={e => {
                                  e.preventDefault();
                                  const target = document.getElementById('keyusecases-roata-container');
                                  if (target) {
                                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                }}
                              >
                                Learn More <span style={{fontSize: '1.1em', marginLeft: 6}}>↗</span>
                              </a>
                              <Link to="/book-demo" className="genix-btn genix-btn-primary" style={{left:'0'}}>
                      Book a Demo ↗
                    </Link>
                            </div>
                          </div>
                          <div className="genix-hero-image genix-roata-hero-image animate-slide-right" style={{flex: 1, minWidth: 320, display: 'flex', justifyContent: 'center'}}>
                            <img
                              src="/images/GenixRoata.png"
                              alt="Genix Rota Dashboard"
                              style={{width: '100%', maxWidth: 800, borderRadius: 18, boxShadow: '0 8px 32px rgba(0,0,0,0.08)'}}
                            />
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                  <div className="scrolling-content">
                    <WhyGenixRota />
                    <KeyUseCasesRota />
                    <HowItWorksRota />
                    <Testimonials customTitle="What our customers are saying" />
                    <RoataPricingCTA />
                    <Footer />
                  </div>
                </div>
              </>} />
              <Route path="/products/genix-pendent" element={<>
                <div className="page-layout">
                  <div className="hero-fixed-background">
                    <ProductHero
                      title="Meet Genix Pendant"
                      description="Genix Pendant is a next-generation wearable device designed to act as an AI-powered healthcare assistant. It transforms clinical conversations into real-time, multilingual, structured, and actionable data, right from the moment it hears your voice."
                      imageSrc="/images/GenixPendentHero.png"
                      imageAlt="Genix Pendant"
                    />
                  </div>
                  <div className="scrolling-content">
                    <div className="component-separator"></div>
                    <DeviceOverview />
                    <div className="component-separator"></div>
                    <DeviceFeatures />
                    <div className="component-separator"></div>
                    <KeyUseCases />
                    <div className="component-separator"></div>
                    <div className="pendent-testimonials">
                      <Testimonials customTitle="What Our Customers Are Saying" />
                    </div>
                    <div className="component-separator"></div>
                    <FutureEnhancements />
                    <div className="component-separator"></div>
                    <TechnologyStack />
                    <div className="component-separator"></div>
                    <Consultation />
                    <Footer />
                  </div>
                </div>
              </>} />
              <Route path="/contact" element={<>
                <div className="page-layout">
                  <div className="hero-fixed-background">
                    <ContactUs />
                  </div>
                  <div className="scrolling-content">
                    <Location />
                    <Footer />
                  </div>
                </div>
              </>} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/login" element={<Login />} />
              <Route path="/book-demo" element={
                <>
                  <div className="page-layout">
                    <div className="hero-fixed-background">
                      <BookDemoHero />
                    </div>
                    <div className="scrolling-content">
                      <ProductImageCarousel />
                      <Testimonials />
                      <BookDemoTeamSection />
                      <Footer />
                    </div>
                  </div>
                </>
              } />
              <Route path="/pricing" element={<PricingRoute />} />
              <Route path="/resources" element={<ResourcesRoute />} />
              <Route path="/try-genix-pendant" element={<TryGenixPendant />} />
            </Routes>
          </main>
        </div>
      </Router>
    </DarkModeProvider>
  )
}

export default App
